import { fetchJsonResponse } from 'typescript-functional-extensions';

export async function patchFormHelper(): Promise<void> {
  if (window.kentico?.updatableFormHelper) {
    const _ = window.kentico.updatableFormHelper.submitForm;

    window.kentico.updatableFormHelper.submitForm = async (
      event: SubmitEvent
    ) => {
      event.preventDefault();
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        console.error('Event target is not a form element.');
        return;
      }

      const formData = new FormData(form);

      try {
        const resp = await fetch(form.action, {
          method: 'POST',
          body: formData,
        });

        if (resp.headers.get('content-type')?.startsWith('application/json')) {
          const result = (await resp.json()) as { redirectTo?: string };

          // Handle Redirect to URL
          if (
            result.redirectTo &&
            !result.redirectTo
              .toLowerCase()
              .startsWith('/content-library/all-form-submission-responses')
          ) {
            window.location.href = result.redirectTo;
            return;
          }

          // Handle Display Text (dynamic success message)
          if (
            result.redirectTo
              ?.toLowerCase()
              .startsWith('/content-library/all-form-submission-responses')
          ) {
            await fetchJsonResponse<FormSubmissionResponseContent>(
              fetch(
                `/api/forms/submission-response?path=${result.redirectTo}`,
                {
                  headers: { contentType: 'application/json' },
                  method: 'GET',
                }
              ),
              (e) =>
                `Could not get form submission content for ${result.redirectTo}: ${e}`
            ).match({
              success: (c) => {
                const replaceTarget =
                  form.getAttribute('data-ktc-ajax-update') ?? '';
                const targetEl = document.getElementById(replaceTarget);

                if (!targetEl) {
                  console.error('Target element for replacement not found.');
                  return;
                }

                // Hide form title on successful submission
                const testTarget =
                  targetEl.parentElement?.previousElementSibling?.querySelector(
                    'div.hide-on-submit'
                  );
                testTarget?.classList.add('hidden');

                // Insert success message
                targetEl.innerHTML = `
                  <div class="text-center px-10">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="w-8 h-8 fill-green-700 mx-auto mb-2">
                      <path d="M243.8 339.8C232.9 350.7 215.1 350.7 204.2 339.8L140.2 275.8C129.3 264.9 129.3 247.1 140.2 236.2C151.1 225.3 168.9 225.3 179.8 236.2L224 280.4L332.2 172.2C343.1 161.3 360.9 161.3 371.8 172.2C382.7 183.1 382.7 200.9 371.8 211.8L243.8 339.8zM512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256zM256 48C141.1 48 48 141.1 48 256C48 370.9 141.1 464 256 464C370.9 464 464 370.9 464 256C464 141.1 370.9 48 256 48z" />
                    </svg>
                    <h3 class="response-title text-green-700">${c.title}</h3>
                    <div class="response-content mt-4">${c.messageHTML}</div>
                  </div>`;
              },
              failure: (e) => {
                console.error('Fetching form submission content failed:', e);
              },
            });
          }
        } else {
          // Handle Reload Form (non-JSON response)
          const replaceTarget = form.getAttribute('data-ktc-ajax-update');
          if (!replaceTarget) {
            console.error('No replace target found for form submission.');
            return;
          }

          const currentScrollPosition = window.scrollY;
          const responseHtml = await resp.text();

          const targetEl = document.getElementById(replaceTarget);
          if (targetEl) {
            targetEl.innerHTML = responseHtml;
            window.scrollTo(0, currentScrollPosition);
          } else {
            console.error(
              'Could not replace form content; target element not found.'
            );
          }

          // Check if form should be reloaded
          if (form.hasAttribute('data-reload-after-submit')) {
            setTimeout(() => window.location.reload(), 500); // Delay reload slightly
          }
        }
      } catch (error) {
        console.error('Form submission failed:', error);
      }
    };
  }
}

// Type for expected JSON response structure
type FormSubmissionResponseContent = {
  title: string;
  messageHTML: string;
};
